import { FORMAT_TIME } from 'constants';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import { videoSamplesDeleteByIds } from 'api/endpoints/video-sample';
import Button from 'components/Button';
import Table, { IndeterminateCheckbox } from 'components/Table';
import { useClickOutside } from 'hooks/useClickOutside';
import { toastSuccess } from 'lib/toast';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { setModalSampleVideo } from 'store/actions/sample-video';
import { convertToQueryString } from 'utils/helper';

const ControlSampleVideoItem = ({ item, deleteSampleVideo, editSampleVideo }) => {
  const [openControlReportItem, setIsOpenControlReportItem] = useState(false);
  const controlReportItemRef = useRef(null);

  useClickOutside(controlReportItemRef, () => setIsOpenControlReportItem(false));

  const imageText = item.file_samples.some((item) => item.file_type === 'image') ? true : false;
  const videoText = item.file_samples.some((item) => item.file_type === 'video') ? true : false;

  return (
    <div className="flex-center-center text-sm">
      <div className="relative flex justify-center items-center">
        <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlReportItem((prev) => !prev)} ref={controlReportItemRef}>
          <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
        </button>
        <div className={`absolute z-2 right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlReportItem ? 'show' : ''}`}>
          <ul>
            <li
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
              onClick={() => editSampleVideo(item.id)}
            >
              {imageText && videoText && '参考動画・画像を編集'}
              {imageText && !videoText && '参考画像を編集'}
              {!imageText && videoText && '参考動画を編集'}
            </li>
            <div className="w-full h-[1px] bg-cyan-591"></div>
            <li
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
              onClick={() => deleteSampleVideo(item)}
            >
              削除
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const SampleVideoTable = (props) => {
  const dispatch = useDispatch();
  const { data, setSort, sort, page, keyword, isFilter, reloadData } = props;
  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);

  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        // Get item index with page number
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        // Create new object and push to array
        if (pageSelectedExists === -1) {
          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        /**
         * If you are select
         * - Delete the selected item from the array
         * - Delete all Object contains lines
         * - Add new object
         */
        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: [...rowSelected, rowSelect.original.id],
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const data = table.getRowModel().rows;
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          return [
            ...prevState,
            {
              page: page,
              selected: data?.map((row) => row?.original?.id),
            },
          ];
        }

        /**
         * If the Select of the page is currently in the Testhall (Condition) state
         * (Condition): Select length is equal to the number of non -public status items on the current page
         * then remove the whole object of that page to become Uncheck.
         */
        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === data?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: data.map((row) => row?.original?.id),
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  const handleVideoSamplesDeleteByIds = (item, idSampleVideo, isMultiple = false) => {
    const confirmDeleteSampleVideo = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        const queryString = convertToQueryString({ video_category_ids: idSampleVideo });

        videoSamplesDeleteByIds(queryString)
          .then(async () => {
            await reloadData();
            dispatch(clearModal());
            dispatch(setLoading({ isOpen: false }));
            if (isMultiple) {
              toastSuccess('選択されたファイルは正常に削除されました。');
              setSelectedRows([]);
            } else {
              toastSuccess('削除が完了しました。');
              setSelectedRows(() => {
                const selectedRowWithPage = selectedRows.concat();

                const pageSelectedExists = _.findIndex(selectedRowWithPage, { page: page });

                if (pageSelectedExists !== -1) {
                  const selectedRemovedRow = selectedRowWithPage[pageSelectedExists]?.selected?.filter((row) => row !== idSampleVideo[0]);
                  selectedRowWithPage?.splice(pageSelectedExists, 1);

                  selectedRowWithPage?.push({
                    page: page,
                    selected: selectedRemovedRow,
                  });
                  return selectedRowWithPage;
                }
                return [];
              });
            }
          })
          .catch((errors) => {
            dispatch(clearModal());
          });
      } catch (error) {
        // Handle Error
        dispatch(setLoading({ isOpen: false }));
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: isMultiple
          ? '複数の動画／画像を削除しようとしています。<br  />本当に削除してもよろしいですか？<br />※削除後は元に戻すことはできません。'
          : `${item?.[0]?.name}を削除してもよろしいですか。`,
        confirmText: 'はい',
        cancelText: 'キャンセル',
        onConfirm: confirmDeleteSampleVideo,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const onClickDeleteSampleVideos = async () => {
    const listSurveysDelete = _.flatMap(selectedRows, 'selected');
    await handleVideoSamplesDeleteByIds([], listSurveysDelete, true);
  };

  const onClickDeleteSampleVideo = async (item) => {
    await handleVideoSamplesDeleteByIds([item], [item.id], false);
  };

  const onClickEditSampleVideo = async (idSampleVideo) => {
    dispatch(setModalSampleVideo({ isOpen: true, isEdit: true, id: idSampleVideo }));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={`${data?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
            <IndeterminateCheckbox
              className={`m-auto ${data?.length <= 0 && '!cursor-not-allowed'}`}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === table.getRowModel().rows?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <IndeterminateCheckbox
                className={'m-auto'}
                {...{
                  checked: _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id),
                  indeterminate: false,
                  onChange: () => handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'name',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>運動カテゴリ</span>;
        },
        cell: ({ row, getValue }) => {
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className={'break-all'}>{getValue()}</span>
            </div>
          );
        },
        size: 320,
        sortingFn: () => {},
      },
      {
        accessorKey: 'explain',
        enableSorting: false,
        header: () => <span>参考動画・画像説明</span>,
        cell: ({ row }) => {
          const explain = row.original.explain.replace(/\n/g, '<br />').replace(/\r/g, '');
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className="break-all" dangerouslySetInnerHTML={{ __html: explain }} />
            </div>
          );
        },
        size: 400,
      },
      {
        accessorKey: 'updated_at',
        header: () => <span>最終編集日</span>,
        cell: ({ row }) => {
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className="break-all">{moment(row.original.updated_at).format(FORMAT_TIME.DATE)}</span>
            </div>
          );
        },
        size: 320,
        sortingFn: () => {},
      },
      {
        accessorKey: '-',
        enableSorting: false,
        header: () => <span>動画・画像ファイル名</span>,
        cell: ({ row }) => {
          return (
            <ul className="text-sm gap-5 list-disc">
              {row.original.file_samples.map((item, index) => (
                <li key={index} className="break-all mt-2">
                  {item.file_custom_url}
                </li>
              ))}
            </ul>
          );
        },
        size: 336,
      },
      {
        accessorKey: '-',
        enableSorting: false,
        header: () => <span>ファイルタイプ</span>,
        cell: ({ row }) => {
          const imageText = row.original.file_samples.some((item) => item.file_type === 'image') ? '画像' : '';
          const videoText = row.original.file_samples.some((item) => item.file_type === 'video') ? '動画' : '';
          return (
            <div className="flex-between-center text-sm">
              <p>
                <span className="break-all first-letter:uppercase">{imageText} </span>
                {imageText && videoText && <span className="break-all first-letter:uppercase">と</span>}
                <span className="break-all first-letter:uppercase">{videoText}</span>
              </p>
            </div>
          );
        },
        size: 144,
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => (
          <ControlSampleVideoItem item={row?.original || {}} deleteSampleVideo={onClickDeleteSampleVideo} editSampleVideo={onClickEditSampleVideo} />
        ),
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword]);

  return (
    <>
      {_.flatMap(selectedRows, 'selected').length > 0 && (
        <>
          <div className="p-4 w-full border-2 border-solid border-primary-423 rounded-md mb-2 flex justify-between items-center h-16">
            <p className="text-secondary-354 text-sm">{_.flatMap(selectedRows, 'selected').length} 件選択中</p>
            <div className="flex">
              <div className="flex gap-4">
                <Button
                  className="text-sm w-21 h-8"
                  color="danger-outline"
                  text={'削除'}
                  onClick={onClickDeleteSampleVideos}
                  icon={<img className="mr-2.5" src={require('assets/resources/Trash.svg').default} alt="trash icon" width={14} height={14} />}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Table columns={columns} data={data} isFilter={isFilter} isSampleVideoList={true} />
    </>
  );
};

export default React.memo(SampleVideoTable);
